import { React } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@fs/zion-router'
import { ResponsiveTableRow, Expander, ExpanderHeader, ExpanderContent, H5 } from '@fs/zion-ui'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { SCREENING_STATE } from '../util/artifactsConstants'
import { truncateString } from '../util/artifactsUtils'
import { toLocaleString } from '../../../util/DateUtil'

/**
 * Displays basic information about the artifact
 */
export default function ArtifactInfoPanel({ artifact }) {
  const MAX_ASSOCIATED_TITLE_LENGTH = 75
  const [t] = useTranslation()
  const title = t('artifacts.artifactInfo', 'Artifact Info')
  const columnHeaders = ['', ' ']
  const rows = [
    {
      id: 'artifactIdCol',
      col1: t('artifacts.artifactId', 'Artifact ID'),
      col2: artifact.id,
    },
    {
      id: 'apidCol',
      col1: t('artifacts.apid', 'APID'),
      col2: artifact.apid,
    },
    {
      id: 'uploadDateCol',
      col1: t('artifacts.uploadDate', 'Upload Date'),
      col2: toLocaleString(new Date(artifact.uploadDatetime), false),
    },
    {
      id: 'processingStateCol',
      col1: t('artifacts.processingState', 'Processing State'),
      col2: t(`artifact.imageProcessingState.${artifact.imageProcessingState}`, artifact.imageProcessingState),
    },
    {
      id: 'screeningStateCol',
      col1: t('artifacts.screeningState', 'Screening State'),
      col2: t(`artifact.screeningState.${artifact.screeningState}`, artifact.screeningState),
    },
    {
      id: 'displayStateCol',
      col1: t('artifacts.displayState', 'Display State'),
      col2: t(`artifact.displayState.${artifact.artifactDisplayState}`, artifact.artifactDisplayState),
    },
    {
      id: 'visibilityCol',
      col1: t('artifacts.visibility', 'Visibility'),
      col2: t(`artifact.visibility.${artifact.visibility}`, artifact.visibility),
    },
  ]
  // Display screening tag, if restricted
  if (artifact.screeningState === SCREENING_STATE.RESTRICTED) {
    rows.push({
      id: 'screeningTagCol',
      col1: t('artifacts.screeningTag', 'Screening Tag'),
      col2: t(`artifact.screeningTag.${artifact.screeningTag}, artifact.screeningTag`),
    })
  }
  // Provide links to associated artifacts, if any
  if (
    artifact.associatedArtifacts &&
    artifact.associatedArtifacts.artifact &&
    artifact.associatedArtifacts.artifact.length
  ) {
    rows.push({
      id: 'associatedArtifactsCol',
      col1: t('artifacts.associatedArtifacts', 'Associated Artifacts'),
      col2: artifact.associatedArtifacts.artifact.map((associatedArtifact) => (
        <div key={associatedArtifact.id}>
          <Link to={`/artifacts/artifact/${associatedArtifact.id}`} target="_blank">
            {associatedArtifact.id}
          </Link>
          {associatedArtifact.title && ` — ${truncateString(associatedArtifact.title, MAX_ASSOCIATED_TITLE_LENGTH)}`}
        </div>
      )),
    })
  }

  return (
    <Expander>
      <ExpanderHeader>
        <H5>{title}</H5>
      </ExpanderHeader>
      <ExpanderContent>
        <DmcResponsiveTable
          totalColumnCount={columnHeaders.length}
          atSizeColumns={{
            default: {
              labels: columnHeaders,
            },
          }}
          separator={<br />}
        >
          {rows.map((row) => (
            <ResponsiveTableRow key={row.id} column1={row.col1} column2={row.col2} />
          ))}
        </DmcResponsiveTable>
      </ExpanderContent>
    </Expander>
  )
}
